import { theme } from '@config/theme';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import { Autocomplete, Button, TextField } from '@mui/material';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMapsStore } from './store';
import { PlaceSearchResult, usePlacesAutocomplete } from './utils';

interface PlacesAutocompleteProps {
	onPlaceSelect?: (place: PlaceSearchResult | null) => void;
	onSave?: (values: any) => void;
	selectedPlaceProp?: PlaceSearchResult | null;
	disabled?: boolean;
}

export const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
	onPlaceSelect,
	selectedPlaceProp = null,
	onSave,
	disabled = false,
}) => {
	const { suggestions, handleInputChange, setSuggestions } = usePlacesAutocomplete();
	const [selectedPlace, setSelectedPlace] = useState<PlaceSearchResult | null>(selectedPlaceProp);

	const { address } = useMapsStore();
	const { t } = useTranslation();

	const formik = useFormik({
		initialValues: {
			address: address,
		},
		onSubmit: (values) => {
			if (onSave) {
				onSave(values);
			}
		},
	});

	const { setFieldValue } = formik;

	useEffect(() => {
		setFieldValue('address', address);
		setSelectedPlace({ address } as PlaceSearchResult);
	}, [address, setFieldValue]);

	return (
		<div className="w-full sm:w-auto px-2 pt-4 sm:p-0 flex items-center justify-center absolute top-[60px] z-[999] left-1/2 translate-x-[-50%] sm:translate-y-10 translate-y-[60px] rounded-xl">
			<div
				className="w-full bg-white sm:w-[420px] shadow-custom-button rounded-lg flex items-center justify-between gap-[10px] p-5"
				style={{ boxShadow: theme.boxShadows.primary }}
			>
				<Autocomplete
					disabled={disabled}
					sx={{
						'&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root, &.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root':
							{
								paddingRight: '14px',
							},
						'& .MuiAutocomplete-inputRoot': {
							paddingRight: '14px !important',
						},
					}}
					size="small"
					fullWidth
					filterOptions={(x) => x} // Disable built-in filtering
					options={suggestions}
					autoComplete
					includeInputInList
					filterSelectedOptions
					value={selectedPlace}
					noOptionsText="No locations"
					getOptionLabel={(option: PlaceSearchResult) => option.address}
					onChange={(event: any, newValue: PlaceSearchResult | null) => {
						setSuggestions([]);
						setSelectedPlace(newValue);
						onPlaceSelect?.(newValue);
						formik.setFieldValue('address', newValue?.address || '');
					}}
					onInputChange={(event, newInputValue) => {
						handleInputChange(newInputValue);
						formik.setFieldValue('address', newInputValue);
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							fullWidth
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<FmdGoodOutlinedIcon
										sx={{
											color: theme.palette.custom.gray,
										}}
									/>
								),
							}}
							name="address"
							label={t('myFarm.farmLocation')}
							placeholder={t('myFarm.farmLocation')}
							variant="outlined"
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
						/>
					)}
				/>
				{onSave && (
					<Button variant="contained" onClick={() => formik.handleSubmit()}>
						{t('save')}
					</Button>
				)}
			</div>
		</div>
	);
};
